import React from "react";
import { useTranslation } from "react-i18next";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import useUser from "../../../../firebase/useUser";
import { useAuth } from "../../../../firebase/useAuth";
import { useLearningModules } from "../../fetchers/learning_modules/useLearningModules";
import useUserLearningModule from "../../fetchers/useUserLearningModule";
import useAveragePrepFqQuestionScores from "../../fetchers/useAveragePrepFqQuestionScores";
import usePrepFqProfile from "../../fetchers/usePrepFqProfile";
import {
  Button,
  CircularProgress,
  Collapse,
  Fade,
  Grid,
  Rating,
  Tooltip,
} from "@mui/material";
import { topicModalStyles } from "../../styles/topicModalStyles";
import { TransitionProps } from "@mui/material/transitions";
import Slide from "@mui/material/Slide";
import Image from "next/image";
import clsx from "clsx";
import usePrepFqAnswers from "../../fetchers/usePrepFqAnswers";
import PrepFqFormMini from "./PrepFqFormMini";
import BmiFormLite from "./BmiFormLite";
import PersonBenchMarkICon from "../../icons/PersonBenchMarkIcon";
import { getRatingText } from "../../fetchers/helpers";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import { convertMsToTimeString } from "../../../../utils/date";
import { format } from "date-fns";
import { enableBodyScroll } from "body-scroll-lock";
import { useState } from "react";
import DialogActions from "@mui/material/DialogActions";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

interface OwnProps {
  open: boolean;
  setOpen(open: boolean): any;
  moduleId: string;
  setModuleId(moduleId: string): any;
  isTourOpen: boolean;
}
type Props = OwnProps;

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...(props as any)} />;
});

const TopicModal: React.FC<Props> = ({
  open,
  setOpen,
  moduleId,
  setModuleId,
  isTourOpen,
}) => {
  const { t } = useTranslation(["common", "dashboard"]);
  const classes = topicModalStyles();
  const [warningOpen, setWarningOpen] = useState(false);
  const [sdmOpen, setSdmOpen] = useState(false);
  const [asipOpen, setAsipOpen] = React.useState(false);
  const [startAsipOpen, setStartAsipOpen] = React.useState(false);
  const authUser = useAuth();
  const { user } = useUser(authUser?.user?.uid);
  const { learningModules, allQuestions } = useLearningModules();
  const learningModule = learningModules.find((learning_module) =>
    moduleId.includes(learning_module.module_id)
  );
  const relatedModules = learningModules.slice().filter((learning_module) => {
    if (learningModule?.related_modules?.includes(learning_module.module_id)) {
      return true;
    } else {
      return false;
    }
  });
  const questions = allQuestions
    ?.slice()
    .filter((question) =>
      question.module_id.includes(learningModule?.module_id)
    );
  const { userModule, isLoading: loadUserModule } = useUserLearningModule(
    user?.user_id,
    moduleId
  );
  const {
    profile,
    isLoading: loadPrepFqProfile,
    isValidating: profileIsValidating,
  } = usePrepFqProfile(user?.user_id);
  const question_id = profile?.scored_results.find((result) => {
    return result.module_id === moduleId;
  })?.question_id;

  const { answers, isLoading: loadAnswers } = usePrepFqAnswers(
    profile?.prep_fq_profile_id,
    questions
  );

  const currentScore = profile?.scored_results?.find(
    (scoredResult) => scoredResult.module_id === moduleId
  )?.scaled_score;

  const { avgQuestionScores, isLoading: loadAvgScores } =
    useAveragePrepFqQuestionScores(
      profile?.prep_fq_profile_id,
      question_id,
      user,
      profile
    );
  const isLoading = Boolean(
    loadUserModule ||
      loadPrepFqProfile ||
      loadAvgScores ||
      loadAnswers ||
      !avgQuestionScores ||
      !avgQuestionScores?.average_score
  );
  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      enableBodyScroll(document.body);
      document.body.style.overflow = "auto";
      document.body.style.maxHeight = "unset";
    }, 1000);
  };
  const handleWarningClose = () => {
    setWarningOpen(false);
  };
  const handleSdmClose = () => {
    setSdmOpen(false);
  };
  const handleAsipClose = () => {
    setAsipOpen(false);
  };

  const handleOpen = () => {
    if (learningModule?.module_id === "sdm") {
      setSdmOpen(true);
    } else if (learningModule?.module_id === "asip") {
      setAsipOpen(true);
    } else if (learningModule?.live) {
      return undefined;
    } else {
      setWarningOpen(true);
    }
  };
  const startAsip = () => {
    setStartAsipOpen(true);
  };
  return (
    <React.Fragment>
      <Dialog
        open={open}
        onClose={handleClose}
        keepMounted={true}
        fullWidth={true}
        maxWidth="md"
        TransitionComponent={Transition}
        classes={{
          paper: classes.root,
        }}
        scroll={isTourOpen ? "paper" : "body"}
      >
        <DialogContent
          classes={{
            root: classes.content,
          }}
          className={"eleventh-step-topic-landing"}
          id="seventeenth-step-topic-close__observe"
        >
          <IconButton
            aria-label="close"
            className={clsx(
              classes.closeButton,
              "seventeenth-step-topic-close"
            )}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
          <Collapse timeout={0} in={isLoading}>
            <Fade in={isLoading}>
              <div style={{ minHeight: 700 }} className={classes.centered}>
                <CircularProgress style={{ color: "white", fontSize: 80 }} />
              </div>
            </Fade>
          </Collapse>
          <Collapse timeout={0} in={!isLoading}>
            <Fade mountOnEnter unmountOnExit in={!isLoading}>
              <div>
                {/* Title */}
                <Grid container>
                  <Grid className={classes.centered} md={2} sm={4} xs={12} item>
                    <div
                      className={clsx(classes.iconContainer, classes.centered)}
                    >
                      <Image
                        alt={learningModule?.module_name}
                        src={learningModule?.icon_url}
                        layout={"fixed"}
                        width={72}
                        height={72}
                      />
                    </div>
                  </Grid>
                  <Grid
                    className={clsx(classes.leftCentered, classes.column)}
                    md={10}
                    sm={8}
                    xs={12}
                    item
                  >
                    <h3 className={classes.title}>
                      {learningModule?.module_name}
                    </h3>
                    <Grid container>
                      <Grid sm={"auto"} xs={12} item>
                        <p
                          className={clsx(
                            classes.mortality,
                            classes.centeredTextMobile
                          )}
                        >
                          {t(
                            `prepFq:topicModal.weighted_scoring.${learningModule?.weighted_scoring?.type}`
                          )}
                        </p>
                      </Grid>
                      <Grid sm={"auto"} xs={12} item>
                        <div
                          className={clsx(
                            classes.ratingsContainer,
                            classes.centeredTextMobile
                          )}
                        >
                          <Rating
                            classes={{
                              iconFilled: classes.starsFilled,
                            }}
                            value={learningModule?.weighted_scoring?.value}
                            max={3}
                            readOnly
                          />
                        </div>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <div className={classes.divider} />
                {/* Preparedness question */}
                <Grid
                  className={"twelfth-step-topic-question"}
                  style={{ width: "100%" }}
                  container
                >
                  <Grid
                    className={clsx(classes.leftCentered, classes.column)}
                    xs={12}
                    item
                  >
                    <p className={classes.question}>
                      {t("prepFq:topicModal.question")}
                    </p>
                    {moduleId === "bmi" ? (
                      <BmiFormLite questions={questions} />
                    ) : (
                      <PrepFqFormMini questions={questions} />
                    )}
                  </Grid>
                </Grid>
                <div className={classes.divider} />
                {/* Stats Grid */}
                <Grid
                  container
                  className={
                    learningModule?.module_id === "asip" ||
                    learningModule?.module_id === "sdm"
                      ? classes.centered
                      : ""
                  }
                >
                  <Grid
                    className={
                      learningModule?.module_id === "asip" ||
                      learningModule?.module_id === "sdm"
                        ? null
                        : classes.dividerRight
                    }
                    sm={6}
                    xs={12}
                    item
                  >
                    <Collapse timeout={0} in={profileIsValidating}>
                      <Fade in={Boolean(profileIsValidating)}>
                        <div
                          className={classes.centered}
                          style={{ width: "100%", minHeight: 238 }}
                        >
                          <CircularProgress style={{ color: "white" }} />
                        </div>
                      </Fade>
                    </Collapse>
                    <Collapse timeout={0} in={!profileIsValidating}>
                      <Fade in={!Boolean(profileIsValidating)}>
                        <Grid container>
                          <Grid className={classes.centered} xs={3} item>
                            <PersonBenchMarkICon
                              className={classes.benchmarkIcon}
                              progress={
                                100 -
                                Math.round(
                                  profile?.scored_results?.find(
                                    (scoredResult) =>
                                      scoredResult.module_id === moduleId
                                  )?.scaled_score
                                )
                              }
                              bodyFill="#fff"
                              headFill="#fff"
                            />
                          </Grid>
                          <Grid
                            className={clsx(classes.centered, classes.column)}
                            xs={9}
                            item
                          >
                            <p
                              className={clsx(
                                classes.resultsTitle,
                                classes.centeredText
                              )}
                            >
                              {answers?.length > 0 &&
                                (answers[0]?.response_value !== null ||
                                  answers[0]?.selection_values?.length > 0) &&
                                typeof currentScore === "number" &&
                                currentScore >= 0 && (
                                  <span>
                                    {t("prepFq:topicModal.scores.title")}
                                  </span>
                                )}
                            </p>
                            <div className={classes.avgScoresRoot}>
                              <Grid
                                className={clsx(
                                  classes.centered,
                                  classes.scoreContainer
                                )}
                                container
                              >
                                <Grid
                                  className={clsx(
                                    classes.centered,
                                    classes.centeredMobile
                                  )}
                                  sm={5}
                                  xs={12}
                                  item
                                >
                                  {answers?.length > 0 &&
                                    (answers[0]?.response_value !== null ||
                                      answers[0]?.selection_values?.length >
                                        0) &&
                                    typeof currentScore === "number" &&
                                    currentScore >= 0 && (
                                      <p
                                        className={clsx(
                                          classes.score,
                                          classes.centeredText
                                        )}
                                      >
                                        {Math.round(currentScore)}
                                        <span className={classes.percentage}>
                                          %
                                        </span>
                                      </p>
                                    )}
                                </Grid>
                                <Grid
                                  className={clsx(
                                    classes.centered,
                                    classes.centeredMobile
                                  )}
                                  sm={7}
                                  xs={12}
                                  item
                                >
                                  <p
                                    className={clsx(
                                      classes.rating,
                                      classes.centeredText
                                    )}
                                  >
                                    {t(
                                      getRatingText(
                                        profile?.scored_results?.find(
                                          (scoredResult) =>
                                            scoredResult.module_id === moduleId
                                        )?.scaled_score,
                                        avgQuestionScores
                                      )
                                    )}
                                  </p>
                                </Grid>
                              </Grid>
                            </div>
                            <div className={classes.avgScoresRoot}>
                              <div className={classes.avgScoresContainer}>
                                <p
                                  className={clsx(
                                    classes.resultsSubTitle,
                                    classes.centeredText
                                  )}
                                >
                                  {t("prepFq:topicModal.scores.subtitle")}
                                </p>
                                <p
                                  className={clsx(
                                    classes.question,
                                    classes.centeredText
                                  )}
                                >
                                  {avgQuestionScores?.average_score
                                    ? Math.round(
                                        avgQuestionScores?.average_score
                                      )
                                    : 0}
                                  %
                                </p>
                              </div>
                            </div>
                          </Grid>
                        </Grid>
                      </Fade>
                    </Collapse>
                  </Grid>
                  {learningModule?.module_id === "asip" ||
                  learningModule?.module_id === "sdm" ? null : (
                    <Grid
                      className={clsx(classes.centered, classes.column)}
                      sm={6}
                      xs={12}
                      item
                    >
                      <div
                        className={clsx(
                          classes.centered,
                          classes.column,
                          "thirteenth-step-topic-stage-of-change"
                        )}
                        style={{ width: "100%" }}
                      >
                        <p className={clsx(classes.question, classes.centered)}>
                          {t("prepFq:topicModal.stageOfChange")}
                        </p>
                        <div
                          className={clsx(
                            classes.stageOfChangeContainer,
                            classes.centered
                          )}
                        >
                          <p className={classes.stageOfChangeText}>
                            {t(
                              `prepFq:stages_of_change.${
                                userModule?.stage_of_change
                                  ? userModule?.stage_of_change
                                  : "blank"
                              }`
                            )}
                          </p>
                        </div>
                      </div>
                      <div
                        className={classes.divider}
                        style={{ maxWidth: "80%" }}
                      />
                      <div
                        style={{ marginBottom: 12 }}
                        className={clsx(classes.centered, classes.row)}
                      >
                        <HourglassEmptyIcon
                          className={clsx(classes.statsIcon, classes.whiteText)}
                        />{" "}
                        <p
                          className={clsx(
                            classes.statsText,
                            classes.centeredText
                          )}
                        >
                          {t("prepFq:topicModal.timeSpent")}:{" "}
                          {userModule?.total_sessions_duration
                            ? convertMsToTimeString(
                                userModule?.total_sessions_duration
                              )
                            : "--"}
                        </p>
                      </div>
                      <div className={clsx(classes.centered, classes.row)}>
                        <EventAvailableIcon
                          className={clsx(classes.statsIcon, classes.whiteText)}
                        />{" "}
                        <p
                          className={clsx(
                            classes.statsText,
                            classes.centeredText
                          )}
                        >
                          {t("prepFq:topicModal.lastAccessed")}:{" "}
                          {userModule?.is_in_progress
                            ? format(
                                userModule?.date_last_visited,
                                "MMM do, yyyy"
                              )
                            : "--"}
                        </p>
                      </div>
                    </Grid>
                  )}
                </Grid>
                <div className={classes.divider} />
                {/* Footer */}
                <Grid container>
                  <Grid
                    className={clsx(
                      classes.leftAligned,
                      classes.row,
                      "fifteenth-step-topic-related-modules"
                    )}
                    md={9}
                    sm={7}
                    xs={12}
                    item
                  >
                    <p className={classes.relatedModulesText}>
                      {t("prepFq:topicModal.related")}:
                    </p>
                    <div
                      className={clsx(
                        classes.leftAligned,
                        classes.row,
                        classes.relatedModulesContainer
                      )}
                    >
                      {relatedModules.map((relatedModule) => {
                        return (
                          <Tooltip
                            title={relatedModule?.module_name}
                            key={relatedModule?.module_id}
                          >
                            <div
                              onClick={() => {
                                setModuleId(relatedModule?.module_id);
                              }}
                              className={clsx(
                                classes.iconContainerRelated,
                                classes.centered
                              )}
                            >
                              <Image
                                alt={`${relatedModule.module_name} Icon`}
                                src={relatedModule?.icon_url}
                                layout={"fixed"}
                                width={42}
                                height={42}
                              />
                            </div>
                          </Tooltip>
                        );
                      })}
                    </div>
                  </Grid>
                  <Grid
                    className={clsx(
                      classes.leftBottomAligned,
                      classes.openModuleContainer
                    )}
                    md={3}
                    sm={5}
                    xs={12}
                    item
                  >
                    <Button
                      className="sixteenth-step-topic-learning-module"
                      fullWidth
                      size="large"
                      color="secondary"
                      variant="contained"
                      href={
                        learningModule?.live &&
                        learningModule?.module_id !== "sdm"
                          ? `${
                              learningModule?.module_link
                            }?email=${encodeURIComponent(user?.email)}`
                          : undefined
                      }
                      onClick={handleOpen}
                    >
                      {t("prepFq:topicModal.open")}
                    </Button>
                  </Grid>
                </Grid>
              </div>
            </Fade>
          </Collapse>
        </DialogContent>
      </Dialog>
      <Dialog
        open={asipOpen}
        onClose={handleAsipClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        keepMounted={false}
      >
        <DialogTitle id="alert-dialog-title" style={{ fontSize: 24 }}>
          {t("prepFq:topicModal.asipModal.title")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            style={{ fontSize: 18 }}
          >
            {t("prepFq:topicModal.asipModal.description")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleAsipClose}
            color="secondary"
            variant="contained"
          >
            {t("prepFq:topicModal.unavailableModal.button")}
          </Button>
          <Button
            color="primary"
            variant="contained"
            href="https://planwellguide.com"
            rel="noreferrer noopener"
            target="_blank"
          >
            {t("prepFq:topicModal.asipModal.button")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={sdmOpen}
        onClose={handleSdmClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        keepMounted={false}
      >
        <DialogTitle id="alert-dialog-title" style={{ fontSize: 24 }}>
          {t("prepFq:topicModal.sdmModal.title")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            style={{ fontSize: 18 }}
          >
            {t("prepFq:topicModal.sdmModal.description")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSdmClose}
            color="secondary"
            variant="contained"
          >
            {t("prepFq:topicModal.unavailableModal.button")}
          </Button>
          <Button
            color="primary"
            variant="contained"
            href="https://planwellguide.com"
            rel="noreferrer noopener"
            target="_blank"
          >
            {t("prepFq:topicModal.sdmModal.button")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={warningOpen}
        onClose={handleWarningClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        keepMounted={false}
      >
        <DialogTitle id="alert-dialog-title" style={{ fontSize: 24 }}>
          {t("prepFq:topicModal.unavailableModal.title")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            style={{ fontSize: 18 }}
          >
            {t("prepFq:topicModal.unavailableModal.description")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleWarningClose}
            color="secondary"
            variant="contained"
          >
            {t("prepFq:topicModal.unavailableModal.button")}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default TopicModal;
